import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { registerServiceWorker } from './lib/pushNotifications';

import Layout from './components/Layout';
import ImpersonationBanner from './components/ImpersonationBanner';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import CallPage from './pages/CallPage';
import DashboardPage from './pages/DashboardPage';
import SessionsPage from './pages/SessionsPage';
import SessionDetailPage from './pages/SessionDetailPage';
import KnowledgePage from './pages/KnowledgePage';
import PromptsPage from './pages/PromptsPage';
import SettingsPage from './pages/SettingsPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminCompaniesPage from './pages/AdminCompaniesPage';
import ProfilePage from './pages/ProfilePage';

import AgentAnalyticsPage from './pages/AgentAnalyticsPage';
import NotificationPrefsPage from './pages/NotificationPrefsPage';

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="flex items-center justify-center h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--accent-primary)', borderTopColor: 'transparent' }} />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/call" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/call" replace />;
  return children;
}

function AppRoutes() {
  return (
    <>
      <ImpersonationBanner />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Protected routes */}
        <Route path="/" element={<ProtectedRoute><Layout><Navigate to="/call" replace /></Layout></ProtectedRoute>} />
        <Route path="/call" element={<ProtectedRoute><Layout><CallPage /></Layout></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Layout><DashboardPage /></Layout></ProtectedRoute>} />
        <Route path="/analytics/agents" element={<ProtectedRoute roles={['ADMIN', 'COMPANY_ADMIN']}><Layout><AgentAnalyticsPage /></Layout></ProtectedRoute>} />
        <Route path="/sessions" element={<ProtectedRoute><Layout><SessionsPage /></Layout></ProtectedRoute>} />
        <Route path="/sessions/:id" element={<ProtectedRoute><Layout><SessionDetailPage /></Layout></ProtectedRoute>} />
        <Route path="/admin/knowledge" element={<ProtectedRoute><Layout><KnowledgePage /></Layout></ProtectedRoute>} />
        <Route path="/admin/prompts" element={<ProtectedRoute><Layout><PromptsPage /></Layout></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute><Layout><SettingsPage /></Layout></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Layout><ProfilePage /></Layout></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><Layout><NotificationPrefsPage /></Layout></ProtectedRoute>} />

        {/* Admin-only routes */}
        <Route path="/admin/users" element={<ProtectedRoute roles={['ADMIN', 'COMPANY_ADMIN']}><Layout><AdminUsersPage /></Layout></ProtectedRoute>} />
        <Route path="/admin/companies" element={<ProtectedRoute roles={['ADMIN']}><Layout><AdminCompaniesPage /></Layout></ProtectedRoute>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/call" replace />} />
      </Routes>
    </>
  );
}

function App() {
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
